<script setup>
// 數字跳動：票數捲進畫面才從 0 一路加上去，之後票數變了（投票成功 +1）再從舊值滾到新值。
// 全站的票數都走這個元件，順便統一補上千位符。
//
// SSR 與 hydration 的第一幀都印最終值：先印 0 再跳，搜尋引擎與關掉 JS 的瀏覽器會看到 0，
// 版面也會因為位數變化抖一下。動畫是掛載之後才開始的。
const props = defineProps({
  value: { type: [Number, String], default: 0 },
  // 首次進場的時間；之後的變動一律用 BUMP_MS，投一票滾一秒太久
  duration: { type: Number, default: 1100 }
})

// 票數變動多半是 +1，滾太久反而看不出是「加了一票」
const BUMP_MS = 600

const target = computed(() => Number(props.value) || 0)
const shown = ref(target.value)
const el = ref(null)

let raf = 0
let io = null
// 進場動畫只跑一次；跑過之後票數再變就是「從舊值滾到新值」
let played = false

const display = computed(() => Math.round(shown.value).toLocaleString('en-US'))

function stop() {
  if (raf) cancelAnimationFrame(raf)
  raf = 0
}

function animate(from, to, ms) {
  stop()
  const start = performance.now()
  const step = (now) => {
    const p = Math.min(1, (now - start) / ms)
    // easeOutCubic：一開始衝得快、尾巴慢慢收，數字才像在「跳」而不是等速跑錶
    shown.value = from + (to - from) * (1 - Math.pow(1 - p, 3))
    if (p < 1) return (raf = requestAnimationFrame(step))
    raf = 0
    shown.value = to
  }
  raf = requestAnimationFrame(step)
}

// 使用者要求減少動態時一律直接給最終值，不做任何跳動
const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

function play() {
  played = true
  if (reduced()) return (shown.value = target.value)
  animate(0, target.value, props.duration)
}

onMounted(() => {
  if (reduced() || typeof IntersectionObserver === 'undefined') return play()

  // 捲到才跑：排行榜與首頁的票數多半在第一屏之外，一載入就播完等於沒播
  io = new IntersectionObserver(
    (entries) => {
      if (!entries.some((e) => e.isIntersecting)) return
      io.disconnect()
      io = null
      play()
    },
    { threshold: 0.2 }
  )
  io.observe(el.value)
})

// 投票成功後票數 +1：從眼前的數字滾到新的，不必等捲動也不從 0 重來
watch(target, (to) => {
  if (!played) return (shown.value = to)
  if (reduced()) return (shown.value = to)
  animate(shown.value, to, BUMP_MS)
})

onBeforeUnmount(() => {
  stop()
  io?.disconnect()
  io = null
})
</script>

<template>
  <span ref="el" class="count-up">{{ display }}</span>
</template>

<style scoped>
/* 等寬數字：跳動時每一位的寬度才不會跟著字形變來變去，整組數字看起來是在滾而不是在抖 */
.count-up {
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum';
}
</style>
